import React, { useState, useEffect } from 'react';
import UserProfile from './UserProfile';
import TabNavigation from './TabNavigation';
import MyServers from './MyServers';
import MyBoosts from './MyBoosts';
import ServerManagement from './ServerManagement';
import './Dashboard.css';

function Dashboard({ authToken, onTokenExpiration }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('servers');
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' or 'server-management'
  const [selectedServer, setSelectedServer] = useState(null);

  const tabs = [
    {
      id: 'servers',
      label: 'My Servers',
      icon: '🖥️'
    },
    {
      id: 'boosts',
      label: 'My Boosts',
      icon: '⚡'
    }
  ];

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('https://api.vmp.ir/dev/api.php?work=getData', {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        onTokenExpiration();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.user) {
        setUserData(data.user);
      } else {
        throw new Error('Invalid user data received');
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchUserData();
    }
  }, [authToken]);

  const handleLogout = () => {
    localStorage.removeItem('vmp_auth_token');
    onTokenExpiration();
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleServerManage = (server) => {
    setSelectedServer(server);
    setCurrentView('server-management');
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setSelectedServer(null);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'servers':
        return <MyServers authToken={authToken} onTokenExpiration={onTokenExpiration} onServerManage={handleServerManage} />;
      case 'boosts':
        return <MyBoosts authToken={authToken} onTokenExpiration={onTokenExpiration} />;
      default:
        return <MyServers authToken={authToken} onTokenExpiration={onTokenExpiration} onServerManage={handleServerManage} />;
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-logo">VMP Dev Dashboard</h1>
          <UserProfile 
            user={loading ? null : userData} 
            onLogout={handleLogout}
          />
        </div>
      </header>
      
      <main className="dashboard-main">
        {currentView === 'server-management' && selectedServer ? (
          <ServerManagement 
            server={selectedServer} 
            onBack={handleBackToDashboard}
            authToken={authToken}
            onTokenExpiration={onTokenExpiration}
          />
        ) : userData && !loading ? (
          <>
            <div className="welcome-section">
              <h2>Welcome back, {userData.username}!</h2>
              <p>Manage your servers and services with ease.</p>
              {error && (
                <div className="error-message">
                  <p>Note: {error}</p>
                </div>
              )}
            </div>
            
            <TabNavigation 
              activeTab={activeTab}
              onTabChange={handleTabChange}
              tabs={tabs}
            />
            
            <div className="tab-content">
              {renderTabContent()}
            </div>
          </>
        ) : loading ? (
          <div className="welcome-section">
            <h2>Loading your profile...</h2>
            <p>Please wait while we fetch your information.</p>
          </div>
        ) : (
          <div className="welcome-section">
            <h2>Welcome to VMP Developer Dashboard</h2>
            <p>You are successfully authenticated and can now access developer tools.</p>
            {error && (
              <div className="error-message">
                <p>Error loading profile: {error}</p>
                <button onClick={fetchUserData} className="retry-button">
                  Retry
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default Dashboard;